import { Modal } from "./base_modal";
import { toggleHidden, clearNode, renderList } from "./utils";
import { getProjectsForBulkConnect, bulkConnectPhones } from "../datatable/free_phones/api/index";
import { LICENSE_OPTIONS, COMMON_ERROR_KEYS } from "../datatable/free_phones/phone_statuses.config";

/** Модальное окно подключения номеров */
export class ConnectModal extends Modal {
  constructor(selectedPhones, selectedCount) {
    super({ className: "bulk-connect-modal" });

    this.selectedPhones = selectedPhones;
    this.selectedCount = selectedCount;
    this.debounceTimeout = null;
    this.emailInput = null;
    this.applyButton = null;
    this.dropdown = null;
    this.phonesContainer = null;
    this.userMessage = null;
  }

  /** Строит HTML-содержимое модального окна
   * @returns {string} HTML-строка с содержимым модального окна
   */
  buildContent() {
    return `
      <h3>Подключить ${this.selectedCount} номеров</h3>
      <div class="connect-form">
        <label for="connect-user-email">Email пользователя</label>
        <div class="connect-input-wrapper">
          <input type="email" id="connect-user-email" placeholder="user@example.com" autocomplete="off" />
          <ul class="projects-dropdown hidden"></ul>
        </div>
        <div class="connect-user-message hidden"></div>
        <div class="selected-phones hidden"></div>
      </div>
      <div class="modal-actions">
        <button class="btn-cancel">Отмена</button>
        <button class="btn-apply">Применить</button>
      </div>
    `;
  }

  /** Получает список проектов пользователя по email
   * @param {string} email - Email пользователя
   * @returns {Promise<void>}
   */
  async getProjects(email) {
    if (!email) {
      this.hideDropdown();
      return;
    }

    try {
      const response = await getProjectsForBulkConnect(email);
      const userData = await response.json();

      if (userData.message) {
        this.renderDropdownMessage(userData.message);
        return;
      }

      this.renderProjects(userData.projects, userData);
    } catch (error) {
      this.renderDropdownMessage(error.message || "Произошла ошибка");
    }
  }

  /** Отображает сообщение в выпадающем списке проектов
   * @param {string} message - Текст сообщения
   * @returns {void}
   */
  renderDropdownMessage(message) {
    this.dropdown.innerHTML = `<li class="projects-dropdown-message">${message}</li>`;
    toggleHidden(this.dropdown, false);
  }

  /** Отображает список проектов в выпадающем списке
   * @param {Object[]} [projects=[]] - Массив проектов пользователя
   * @param {Object} userData - Данные пользователя
   * @returns {void}
   */
  renderProjects(projects = [], userData) {
    if (!projects.length) {
      this.renderDropdownMessage("Ничего не найдено");
      return;
    }

    this.dropdown.innerHTML = renderList(
      projects,
      project => `
        <li class="projects-dropdown-item" data-id="${project.id}" data-email="${userData.user_email}" data-type="${userData.user_owner}">
          ${project.title}
        </li>
      `
    );

    toggleHidden(this.dropdown, false);
  }

  /** Скрывает выпадающий список проектов
   * @returns {void}
   */
  hideDropdown() {
    clearNode(this.dropdown);
    toggleHidden(this.dropdown, true);
  }

  /** Отображает карточки телефонов для подключения
   * @param {string} userType - Тип пользователя (beeline, elama, gudok и т.д.)
   * @returns {void}
   */
  renderPhones(userType) {
    if (!this.selectedPhones.length) {
      toggleHidden(this.phonesContainer, true);
      return;
    }

    const isBeeline = userType === "beeline";

    this.phonesContainer.innerHTML = renderList(
      this.selectedPhones,
      phone => `
        <div class="phone-connect-card" data-phone-id="${phone.id}" data-phone="${phone.label}">
          <div class="phone-connect-header">
            ${phone.label} (оператор: ${phone.operator})
          </div>
          <div class="phone-connect-options">
            <div class="settings-block">
              ${!isBeeline ? this.renderAutoRenewBlock() : ""}
              ${this.renderLicenseBlock()}
            </div>
            ${!isBeeline ? this.renderServicesBlock() : ""}
          </div>
        </div>
      `
    );

    toggleHidden(this.phonesContainer, false);
  }

  /** Рендерит блок автопродления
   * @returns {string} HTML-строка с чекбоксом автопродления
   */
  renderAutoRenewBlock() {
    return `
      <div class="auto-renew-block">
        <label class="checkbox-row">
          <input type="checkbox" class="auto-renew-checkbox" />
          Автопродление
        </label>
      </div>
    `;
  }

  /** Рендерит блок выбора лицензии
   * @returns {string} HTML-строка с селектом выбора лицензии
   */
  renderLicenseBlock() {
    return `
      <div class="license-block">
        <label>Лицензия</label>
        <select class="license-select">
          ${renderList(LICENSE_OPTIONS, option => `<option value="${option}">${option}</option>`)}
        </select>
      </div>
    `;
  }

  /** Рендерит блок дополнительных услуг
   * @returns {string} HTML-строка с чекбоксами услуг
   */
  renderServicesBlock() {
    return `
      <div class="services-block">
        <span class="services-title">Услуги</span>
        <label class="checkbox-row">
          <input type="checkbox" class="service-checkbox" data-service="vats" />
          ВАТС
        </label>
        <label class="checkbox-row">
          <input type="checkbox" class="service-checkbox" data-service="call_record" />
          Записи разговора
        </label>
      </div>
    `;
  }

  /** Собирает данные всех телефонов из формы подключения
   * @returns {Object[]} Массив объектов с данными телефонов
   */
  getPhonesRecords() {
    return [...this.modal.querySelectorAll(".phone-connect-card")].map(card => ({
      id: Number(card.dataset.phoneId),
      auto_renew: card.querySelector(".auto-renew-checkbox")?.checked || false,
      services: {
        vats: card.querySelector('[data-service="vats"]')?.checked || false,
        call_record: card.querySelector('[data-service="call_record"]')?.checked || false,
      },
      license: Math.max(30, Number(card.querySelector(".license-select")?.value) || 30),
    }));
  }

  /** Очищает все ошибки валидации телефонов в модальном окне
   * @returns {void}
   */
  clearPhoneErrors() {
    this.modal.querySelectorAll(".phone-error").forEach(node => node.remove());
  }

  /** Отображает ошибки валидации для конкретных телефонов
   * @param {Object.<string, string|string[]>} message - Объект с ошибками по телефонам
   * @returns {void}
   */
  renderPhoneErrors(message) {
    if (!message || typeof message !== "object") return;

    this.clearPhoneErrors();

    Object.entries(message).forEach(([key, errors]) => {
      if (COMMON_ERROR_KEYS.includes(key)) return;

      const card = this.modal.querySelector(`.phone-connect-card[data-phone="${key}"]`);
      if (!card) return;

      card.insertAdjacentHTML(
        "beforeend",
        `<div class="phone-error">${renderList(this.normalizeArray(errors), error => `<span>${error}</span>`)}</div>`
      );
    });
  }

  /** Преобразует значение в массив
   * @param {any} value - Значение для нормализации
   * @returns {Array} Нормализованный массив
   */
  normalizeArray(value) {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
  }

  /** Извлекает общие ошибки подключения из ответа API
   * @param {Object.<string, string|string[]>} message - Объект с ошибками от API
   * @returns {string|null} Строка с общими ошибками или null
   */
  getBulkConnectCommonErrors(message) {
    if (!message || typeof message !== "object") return null;

    const errors = COMMON_ERROR_KEYS.flatMap(key => this.normalizeArray(message[key])).filter(Boolean);
    return errors.length ? errors.join("<br />") : null;
  }

  /** Устанавливает сообщение для пользователя
   * @param {string} message - Текст сообщения
   * @returns {void}
   */
  setUserMessage(message) {
    this.userMessage.textContent = message;
    toggleHidden(this.userMessage, !message);
  }

  /** Обработчик клика вне области ввода email
   * @param {MouseEvent} event - Событие клика
   * @returns {void}
   */
  handleOutsideClick(event) {
    if (!event.target.closest(".connect-input-wrapper")) {
      this.hideDropdown();
    }
  }

  /** Обработчик нажатия кнопки "Применить"
   * @returns {Promise<void>}
   */
  async handleApply() {
    const email = this.emailInput.value.trim();
    const projectId = this.emailInput.dataset.projectId;

    if (!email || !projectId) {
      toastr.error("Выберите проект", "error");
      return;
    }

    try {
      this.applyButton.disabled = true;
      this.clearPhoneErrors();

      const data = await bulkConnectPhones({
        email,
        project_id: projectId,
        phonesRecords: this.getPhonesRecords(),
      });

      if (data.success) {
        this.cleanup();
        this.close(true);
        return;
      }

      this.renderPhoneErrors(data.message);

      const commonErrors = this.getBulkConnectCommonErrors(data.message);
      if (commonErrors) {
        toastr.error(commonErrors, "error");
      }
    } catch (error) {
      console.error(error);
      toastr.error(error?.message || "Произошла ошибка", "error");
    } finally {
      this.applyButton.disabled = false;
    }
  }

  /** Обработчик ввода email с debounce
   * @param {Event} event - Событие ввода
   * @returns {void}
   */
  handleEmailInput(event) {
    const email = event.target.value.trim();

    clearTimeout(this.debounceTimeout);
    this.debounceTimeout = setTimeout(() => {
      this.getProjects(email);
    }, 400);
  }

  /** Обработчик выбора проекта из выпадающего списка
   * @param {MouseEvent} event - Событие клика
   * @returns {void}
   */
  handleProjectSelect(event) {
    const item = event.target.closest(".projects-dropdown-item");
    if (!item) return;

    const userType = item.dataset.type || "gudok";

    this.emailInput.value = item.dataset.email || "";
    this.emailInput.dataset.projectId = item.dataset.id || "";
    this.emailInput.dataset.userType = userType;

    this.hideDropdown();
    this.setUserMessage("");

    if (userType === "elama") {
      this.applyButton.disabled = true;
      this.setUserMessage("Пользователям elama недоступно подключение номеров");
      clearNode(this.phonesContainer);
      toggleHidden(this.phonesContainer, true);
      return;
    }

    this.applyButton.disabled = false;
    this.renderPhones(userType);
  }

  /** Настраивает обработчики событий модального окна
   * @param {Object} params - Параметры настройки
   * @param {HTMLElement} params.modal - DOM-элемент модального окна
   * @param {Function} params.close - Функция закрытия модального окна
   * @returns {void}
   */
  setupHandlers({ modal, close }) {
    this.modal = modal;
    this.emailInput = modal.querySelector("#connect-user-email");
    this.applyButton = modal.querySelector(".btn-apply");
    this.dropdown = modal.querySelector(".projects-dropdown");
    this.phonesContainer = modal.querySelector(".selected-phones");
    this.userMessage = modal.querySelector(".connect-user-message");

    this.emailInput?.addEventListener("input", e => this.handleEmailInput(e));
    this.dropdown?.addEventListener("click", e => this.handleProjectSelect(e));

    document.addEventListener("click", e => this.handleOutsideClick(e));

    modal.querySelector(".btn-cancel")?.addEventListener("click", () => {
      this.cleanup();
      close(null);
    });

    this.applyButton?.addEventListener("click", () => this.handleApply());
  }

  /** Очищает таймеры и обработчики событий
   * @returns {void}
   */
  cleanup() {
    clearTimeout(this.debounceTimeout);
    document.removeEventListener("click", e => this.handleOutsideClick(e));
  }

  /** Отображает модальное окно подключения
   * @returns {Promise<boolean|null>} Promise, возвращающий результат действия
   */
  async show() {
    return this.open({
      content: this.buildContent(),
      onInit: ({ modal, close }) => this.setupHandlers({ modal, close }),
    });
  }
}
